<script setup lang="ts">
import { computed, nextTick, ref, watch } from "vue";
import { useRoute, useRouter } from "vue-router";
import { ElMessage, ElMessageBox } from "element-plus";
import {
  Bell,
  BookOpenCheck,
  Building2,
  ChevronDown,
  ChevronRight,
  ClipboardList,
  FileInput,
  FileStack,
  Files,
  FileText,
  LayoutDashboard,
  LogOut,
  Menu,
  Moon,
  PanelLeftClose,
  PanelLeftOpen,
  ReceiptText,
  ScrollText,
  Settings2,
  ShieldCheck,
  Sun,
  WalletCards,
  HandCoins,
  Banknote,
  Landmark,
  Calculator,
  ListChecks,
  UsersRound,
  BadgeCheck,
  Monitor,
  FolderKanban,
  MessageCircle,
} from "lucide-vue-next";
import ProductBrand from "@/components/ProductBrand.vue";
import { useTheme } from "@/composables/useTheme";
import { useSessionStore } from "@/stores/session";

type MenuItem = {
  path: string;
  label: string;
  icon: typeof LayoutDashboard;
  permission?: string;
  permissions?: string[];
};

const route = useRoute();
const router = useRouter();
const session = useSessionStore();
const { isDark, toggleTheme } = useTheme();
const mobileOpen = ref(false);
const switchingOrganization = ref(false);
const collapsed = ref(readCollapsed());

const navigation: Array<{ label: string; items: MenuItem[] }> = [
  {
    label: "概览",
    items: [
      { path: "/workbench", label: "工作台", icon: LayoutDashboard },
      { path: "/assistant", label: "etax 办税助手", icon: MessageCircle, permission: "assistant:read" },
    ],
  },
  {
    label: "发票业务",
    items: [
      {
        path: "/invoices",
        label: "发票台账",
        icon: ReceiptText,
        permission: "invoice:read",
      },
      {
        path: "/imports",
        label: "发票导入",
        icon: FileInput,
        permissions: ["invoice:import", "invoice:review"],
      },
      { path: "/invoice-requests", label: "开票申请", icon: FileText },
    ],
  },
  {
    label: "账务协作",
    items: [
      {
        path: "/bills",
        label: "账单与分摊",
        icon: WalletCards,
        permission: "bill:write",
      },
      {
        path: "/reimbursements",
        label: "公司报销",
        icon: HandCoins,
        permission: "reimbursement:read",
      },
      { path: "/projects", label: "项目管理", icon: FolderKanban, permission: "project:read" },
      { path: "/fixed-assets", label: "固定资产", icon: Monitor, permission: "asset:read" },
      { path: "/receivables", label: "应收台账", icon: Banknote, permission: "receivable:read" },
      { path: "/funds", label: "资金核对", icon: Landmark, permission: "funds:read" },
      { path: "/bank-receipts", label: "银行回单", icon: Landmark, permission: "funds:read" },
      { path: "/contracts", label: "合同管理", icon: ScrollText },
      { path: "/finance", label: "财务结账", icon: Calculator, permission: "invoice:read" },
      { path: "/tax-preparations", label: "申报与文书", icon: ListChecks, permission: "invoice:read" },
      { path: "/tax-certificates", label: "完税证明", icon: FileText, permission: "tax-certificate:read" },
      { path: "/tax-tasks", label: "办税事项", icon: ClipboardList },
    ],
  },
  {
    label: "资料与记录",
    items: [
      { path: "/tax-logs", label: "办税日志", icon: BookOpenCheck, permission: "tax-log:read" },
      {
        path: "/files",
        label: "文件中心",
        icon: Files,
        permission: "file:download",
      },
      {
        path: "/exports",
        label: "导出任务",
        icon: FileStack,
        permission: "invoice:export",
      },
      {
        path: "/audit-logs",
        label: "审计日志",
        icon: ShieldCheck,
        permission: "audit:read",
      },
    ],
  },
  {
    label: "企业管理",
    items: [
      { path: "/business-license", label: "营业执照", icon: BadgeCheck, permission: "file:download" },
      { path: "/personnel", label: "受雇人员", icon: UsersRound, permission: "personnel:read" },
      { path: "/settings", label: "企业设置", icon: Settings2 },
    ],
  },
];

const visibleNavigation = computed(() =>
  navigation
    .map((group) => ({
      ...group,
      items: group.items.filter((item) =>
        item.permissions
          ? session.canAny(...item.permissions)
          : session.can(item.permission),
      ),
    }))
    .filter((group) => group.items.length > 0),
);
const visibleMenus = computed(() =>
  visibleNavigation.value.flatMap((group) => group.items),
);
const activeMenu = computed(
  () =>
    visibleMenus.value.find((item) => route.path === item.path)?.path ??
    visibleMenus.value.find((item) => route.path.startsWith(`${item.path}/`))
      ?.path ??
    (route.path.startsWith("/bill-imports/") ? "/bills" : undefined),
);
const breadcrumbGroup = computed(() => String(route.meta.group ?? "财务管理"));

function readCollapsed(): boolean {
  try {
    return window.localStorage.getItem("etax.ui.sidebar-collapsed") === "true";
  } catch {
    return false;
  }
}

function toggleSidebar(): void {
  collapsed.value = !collapsed.value;
  try {
    window.localStorage.setItem(
      "etax.ui.sidebar-collapsed",
      String(collapsed.value),
    );
  } catch {
    // Sidebar remains usable when storage is unavailable.
  }
}

watch(
  () => route.fullPath,
  () => {
    mobileOpen.value = false;
  },
);

async function changeOrganization(id: string): Promise<void> {
  if (id === session.currentOrgId) return;
  try {
    await ElMessageBox.confirm(
      "切换企业将取消当前请求并清除当前企业页面状态，未保存内容不会保留。",
      "切换企业",
      {
        confirmButtonText: "确认切换",
        cancelButtonText: "留在当前企业",
        type: "warning",
      },
    );
    switchingOrganization.value = true;
    // Dispose invoice/PDF resources before the organization-keyed view changes.
    await nextTick();
    session.switchOrganization(id);
    ElMessage.success("企业已切换");
    await router.replace("/workbench");
    window.location.reload();
  } catch {
    switchingOrganization.value = false;
    /* user cancelled */
  }
}

async function logout(): Promise<void> {
  await session.logout();
  await router.replace("/login");
}
</script>

<template>
  <div class="app-shell" :class="{ 'sidebar-collapsed': collapsed }">
    <aside class="sidebar desktop-sidebar">
      <router-link class="brand" to="/workbench" aria-label="云比特财务管理工作台">
        <ProductBrand :compact="collapsed" decorative />
      </router-link>
      <nav class="nav-groups" aria-label="主导航">
        <div
          v-for="group in visibleNavigation"
          :key="group.label"
          class="nav-group"
        >
          <div class="nav-caption">{{ group.label }}</div>
          <router-link
            v-for="item in group.items"
            :key="item.path"
            :to="item.path"
            :title="collapsed ? item.label : undefined"
            :aria-label="item.label"
            class="nav-item"
            :class="{ 'router-link-active': activeMenu === item.path }"
          >
            <component :is="item.icon" :size="18" :stroke-width="1.8" />
            <span class="nav-text">{{ item.label }}</span>
          </router-link>
        </div>
      </nav>
      <div class="sidebar-bottom">
        <div class="workspace-mark">
          <span class="workspace-icon"><Building2 :size="16" /></span>
          <div>
            <strong>{{ session.currentOrganization?.name ?? "当前企业" }}</strong>
            <span>{{ session.currentOrganization?.role ?? "企业工作空间" }}</span>
          </div>
        </div>
        <button
          class="collapse-button pressable"
          type="button"
          :aria-label="collapsed ? '展开侧边栏' : '收起侧边栏'"
          @click="toggleSidebar"
        >
          <component
            :is="collapsed ? PanelLeftOpen : PanelLeftClose"
            :size="18"
          />
          <span>收起侧边栏</span>
        </button>
      </div>
    </aside>

    <div class="main-shell">
      <header class="topbar">
        <button
          class="icon-button mobile-menu pressable"
          type="button"
          aria-label="打开导航"
          @click="mobileOpen = true"
        >
          <Menu :size="21" />
        </button>
        <div class="breadcrumb" aria-label="面包屑">
          <span>{{ breadcrumbGroup }}</span>
          <ChevronRight :size="14" />
          <strong>{{ route.meta.title ?? "工作台" }}</strong>
        </div>

        <div class="topbar-actions">
          <div class="org-switcher">
            <span class="topbar-label">当前企业</span>
            <el-select
              :model-value="session.currentOrgId"
              aria-label="切换当前企业"
              @change="changeOrganization"
            >
              <el-option
                v-for="org in session.organizations"
                :key="org.id"
                :label="org.name"
                :value="org.id"
              >
                <div class="org-option">
                  <span>{{ org.name }}</span><small>{{ org.role }}</small>
                </div>
              </el-option>
            </el-select>
            <span class="review-badge">
              <BookOpenCheck :size="12" />
              {{
                session.currentOrganization?.reviewMode === "SINGLE"
                  ? "单人复核"
                  : "双人复核"
              }}
            </span>
          </div>
          <button
            class="icon-button pressable"
            type="button"
            :aria-label="isDark ? '切换浅色主题' : '切换深色主题'"
            :title="isDark ? '切换浅色主题' : '切换深色主题'"
            @click="toggleTheme"
          >
            <Sun v-if="isDark" :size="18" />
            <Moon v-else :size="18" />
          </button>
          <button
            class="icon-button notification-button pressable"
            type="button"
            aria-label="通知中心"
            title="通知中心"
            @click="router.push('/notifications')"
          >
            <Bell :size="18" />
          </button>
          <span class="topbar-divider" />
          <el-dropdown trigger="click">
            <button
              class="user-trigger account-button pressable"
              type="button"
              :aria-label="`${session.user?.displayName ?? '用户'}账户菜单`"
            >
              <span class="avatar">{{
                session.user?.displayName?.slice(0, 1) ?? "用"
              }}</span>
              <span class="account-copy">
                <strong>{{ session.user?.displayName }}</strong>
                <small>{{ session.currentOrganization?.role }}</small>
              </span>
              <ChevronDown :size="13" />
            </button>
            <template #dropdown>
              <el-dropdown-menu>
                <el-dropdown-item @click="router.push('/settings')">
                  <Settings2 :size="14" />企业设置
                </el-dropdown-item>
                <el-dropdown-item divided @click="logout">
                  <LogOut :size="14" />退出登录
                </el-dropdown-item>
              </el-dropdown-menu>
            </template>
          </el-dropdown>
        </div>
      </header>

      <main id="main-content" class="main-content workspace" :aria-busy="switchingOrganization">
        <router-view v-if="!switchingOrganization" :key="session.currentOrgId" />
      </main>
      <footer class="app-footer">
        <span>云比特财务管理 · 涉税事务协同管理</span>
        <span>外部税务与云平台操作需人工核验</span>
      </footer>
    </div>

    <el-drawer
      v-model="mobileOpen"
      direction="ltr"
      size="min(296px, 86vw)"
      title="云比特财务管理"
      class="mobile-nav-drawer"
    >
      <template #header="{ titleId, titleClass }">
        <span :id="titleId" :class="titleClass"><ProductBrand /></span>
      </template>
      <nav class="nav-groups" aria-label="移动端主导航">
        <div
          v-for="group in visibleNavigation"
          :key="group.label"
          class="nav-group"
        >
          <div class="nav-caption">{{ group.label }}</div>
          <router-link
            v-for="item in group.items"
            :key="item.path"
            :to="item.path"
            class="nav-item"
            :class="{ 'router-link-active': activeMenu === item.path }"
            @click="mobileOpen = false"
          >
            <component :is="item.icon" :size="19" />
            <span>{{ item.label }}</span>
          </router-link>
        </div>
      </nav>
    </el-drawer>
  </div>
</template>
